import dayjs from 'dayjs'
import { truncate } from 'lodash-es'
import { memo, useCallback } from 'react'
import { LuClock, LuClockArrowDown, LuClockArrowUp, LuEye, LuTrash2 } from 'react-icons/lu'

import { IconButton } from '@/lib/core/components/Button'
import { CircleAvatar } from '@/lib/core/components/CircleAvatar'
import { Actions, Td, Tr } from '@/lib/core/components/Table'
import { Owner } from '@/lib/core/components/Table/Table.styles'
import { SHORT_DATE_TIME_FORMAT } from '@/lib/core/constants/constants'

import { formatDateTime } from '@/lib/utils/date-time'

import { TaskDefinition } from '../../types/task'
import { findTaskSchedule } from '../../utils/taskSchedule'
import { AssignedCompanyPopout } from '../AssignedCompanyPopout'
import { AssignedVehiclePopout } from '../AssignedVehiclePopout'
import { TaskLocationPopout } from '../TaskLocationPopout'
import { TaskProgressCell } from '../TaskProgressCell'
import { TaskStatusDropdown } from '../TaskStatusDropdown/TaskStatusDropdown'
import { TaskStopsPopout } from '../TaskStopsPopout'
import styles from './ExecutionBoard.module.css'
import {
  canDeleteTask,
  canViewTaskDetails,
  getStopsCount,
  isStatusDropdownDisabled,
} from './ExecutionBoard.utils'

interface TaskRowProps {
  task: TaskDefinition
  tripProgress: number | undefined
  onRefresh: () => void
  onViewTask: (task: TaskDefinition) => void
  onDelete: (task: TaskDefinition) => void
}

const getLocationFrom = (task: TaskDefinition) =>
  task.taskDefinition.locations.find((location) => location.predecessor === null)

const getLocationTo = (task: TaskDefinition) => {
  const predecessorLocation = getLocationFrom(task)
  return task.taskDefinition.locations.find(
    (location) => location.predecessor === predecessorLocation?.id,
  )
}

const DescriptionCell = ({ value }: { value: string | undefined }) => {
  const trimmedValue = value?.trim() ?? ''
  if (!trimmedValue) {
    return (
      <span title="-" className={styles.description}>
        -
      </span>
    )
  }
  const truncated = truncate(trimmedValue, { length: 50, omission: '...' })
  return (
    <span title={trimmedValue} className={styles.description}>
      {truncated}
    </span>
  )
}

/** An actual time that landed after the scheduled one, and one that did not. */
const ACTUAL_LATE_COLOR = '#EB3F3F'
const ACTUAL_ON_TIME_COLOR = '#6AB62E'

const LocationDateCell = ({
  task,
  position,
  onRefresh,
}: {
  task: TaskDefinition
  position: 'from' | 'to'
  onRefresh: () => void
}) => {
  const location = position === 'from' ? getLocationFrom(task) : getLocationTo(task)
  const schedule = findTaskSchedule(task)
  const taskDatetime = position === 'from' ? schedule?.start : schedule?.end
  const actualEventDatetime =
    position === 'from' ? location?.actualDepartureDatetime : location?.actualArrival
  const infoText = position === 'from' ? 'Actual departure:' : 'Actual arrival:'

  // The scheduled time keeps a plain clock. On the actual time the arrow says which event
  // the row is for, and the colour says whether it happened late — the two used to be one
  // solid clock told apart by colour alone.
  const ActualClock = position === 'from' ? LuClockArrowUp : LuClockArrowDown
  const isLate =
    actualEventDatetime != null &&
    taskDatetime != null &&
    dayjs(actualEventDatetime).isAfter(dayjs(taskDatetime))

  return (
    <div className={styles.locationDateCell}>
      <TaskLocationPopout mode="EDIT" task={task} locationInput={location} onUpdated={onRefresh} />
      <span
        className={styles.dateWithIcon}
        title={formatDateTime(taskDatetime, SHORT_DATE_TIME_FORMAT)}
      >
        <LuClock />
        <span className={styles.dateText}>
          {formatDateTime(taskDatetime, SHORT_DATE_TIME_FORMAT)}
        </span>
      </span>

      <span
        className={styles.dateWithIcon}
        title={`${infoText} ${formatDateTime(actualEventDatetime, SHORT_DATE_TIME_FORMAT)}`}
      >
        {actualEventDatetime ? (
          <>
            <ActualClock color={isLate ? ACTUAL_LATE_COLOR : ACTUAL_ON_TIME_COLOR} />
            <span className={styles.dateText}>
              {formatDateTime(actualEventDatetime, SHORT_DATE_TIME_FORMAT)}
            </span>
          </>
        ) : (
          <>
            <ActualClock />
            <span className={styles.dateText}>--: --</span>
          </>
        )}
      </span>
    </div>
  )
}

export const TaskRow = memo(
  ({ task, tripProgress, onRefresh, onViewTask, onDelete }: TaskRowProps) => {
    // Stable no-arg wrapper — prevents child callbacks that pass args (e.g. onStatusUpdated,
    // onUpdated) from forwarding those args into SWR's mutate, which would corrupt the cache.
    const handleRefresh = useCallback(() => onRefresh(), [onRefresh])

    return (
      <Tr data-testid={`task-row-${task.id}`}>
        <Td data-testid={`task-reference-${task.id}`}>{task.taskDefinition.reference || '-'}</Td>
        <Td>
          <Owner>
            <CircleAvatar src={task.assignedByOrg.avatar} placeholder="empty" size={27.5} />
            {task.assignedByOrg.name || '-'}
          </Owner>
        </Td>
        <Td
          title={
            task.taskDefinition.description && task.taskDefinition.description.trim() !== ''
              ? task.taskDefinition.description
              : '-'
          }
        >
          <DescriptionCell value={task.taskDefinition.description} />
        </Td>
        <Td>
          <LocationDateCell task={task} position="from" onRefresh={handleRefresh} />
        </Td>
        <Td>
          <LocationDateCell task={task} position="to" onRefresh={handleRefresh} />
        </Td>
        <Td>
          <TaskStopsPopout
            task={task}
            count={getStopsCount(task)}
            testId={`task-stops-${task.id}`}
          />
        </Td>
        <Td>
          <AssignedCompanyPopout task={task} onUpdated={handleRefresh} />
        </Td>
        <Td>
          <AssignedVehiclePopout task={task} onUpdated={handleRefresh} />
        </Td>
        <Td>
          <TaskStatusDropdown
            task={task}
            currentStatus={task.status}
            disabled={isStatusDropdownDisabled(task)}
            onStatusUpdated={handleRefresh}
            testId={`task-status-${task.id}`}
          />
        </Td>
        <Td>
          <TaskProgressCell task={task} tripProgress={tripProgress} />
        </Td>
        <Td>
          <Actions>
            <IconButton
              data-testid={`open-map-task-${task.id}-button`}
              type="button"
              $variant="primary"
              onClick={() => onViewTask(task)}
              disabled={!canViewTaskDetails(task)}
              title={
                canViewTaskDetails(task)
                  ? 'View task details'
                  : 'Only available for scheduled, in-progress or completed tasks'
              }
            >
              <LuEye size="24" />
            </IconButton>
            {canDeleteTask(task) && (
              <IconButton
                data-testid={`delete-task-${task.id}-button`}
                type="button"
                $variant="primary"
                onClick={() => onDelete(task)}
              >
                <LuTrash2 size="24" />
              </IconButton>
            )}
          </Actions>
        </Td>
      </Tr>
    )
  },
)
TaskRow.displayName = 'TaskRow'
